import _ from 'lodash';
import STRATEGIES from 'src/components/plaid/datasource/scenario-environments';
import { timestamp } from 'src/utils/firebase';
import { divisorForPeriod } from 'src/utils/rollup';

// Stacks the assumptions
export class ScenarioStack {
  constructor(scenarios, activeId, options) {
    this.scenarios = scenarios;
    this.options = options;
    this.strategy = options?.strategy ?? 'conservative';

    // hard coded values
    this.system_assumptions = Object.fromEntries(
      Object.entries(STRATEGIES).map(([key, value]) => [key, value[this.strategy]])
    );

    // user's default/base case scenario assumptions
    this.base_assumption = scenarios?.find((e) => e.id === 'default') ?? {};
    // the active scenario applied on top of the others
    this.scenario_assumption = scenarios?.find((e) => e.id === activeId) ?? {};

    // next we'll set the placeholder value for the users assets
    this.system_assumptions.propertyValue = options?.real_estate_value || 0;
    this.system_assumptions.privateAssetsValue = options?.private_asset_value || 0;
  }

  compileStack() {
    const { system_assumptions, base_assumption, scenario_assumption } = this;
    // we'll assemble a stack of assumptions, first using the hardcoded, system default values
    const results = { ...system_assumptions };

    // next we'll apply  the users default assumptions
    Object.entries(base_assumption ?? {}).forEach(([key, value]) => {
      if (_.isEmpty(value)) results[key] = value;
    });

    // lastly we'll apply the currently active scenario, overwriting any previous values
    Object.entries(scenario_assumption ?? {}).forEach(([key, value]) => {
      if (_.isEmpty(value)) results[key] = value;
    });

    return {
      // hard coded values
      systemAssumptions: system_assumptions,
      // user's default/base case scenario assumptions
      baseAssumptions: base_assumption,
      // the active scenario applied on top of the others
      scenarioAssumptions: scenario_assumption,
      // the assumptions model
      keyAssumptions: results,
    };
  }
}

export const getDefaultAssumptions = (data, useDefaults) => {
  const getValue = (defaultValue) => (useDefaults ? defaultValue : null);

  const results = {
    dateOfBirth: getValue(timestamp(new Date('01/15/1973'))),
    targetRetirementAge: getValue(65),
    propertyValue: getValue(0),
    privateAssetsValue: getValue(0.0),
    averageIncomeGrowthRate: getValue(1.03),
    taxRateRetention: getValue(0.65),
    annualInflationRate: getValue(1.025),
    averageEquityGrowthRate: getValue(1.025),
    averagePrivateAssetGrowthRate: getValue(1.015),
    averageRealEstateGrowthRate: getValue(1.02),
    averageEquityWithdrawalRate: getValue(0.005),
    socialSecurityIncome: getValue(1750.0),
    averageRetirementEquityWithdrawalRate: getValue(0.05),
  };

  Object.entries(data ?? {}).forEach(([key, value]) => {
    if (value) results[key] = value;
  });

  return results;
};

export default function useKeyAssumptions(period = 'year', user, useDefaults = true) {
  const defaults = getDefaultAssumptions(
    { ...user?.keyAssumptions, dateOfBirth: user?.dob ?? timestamp(new Date('01/15/1973')) },
    useDefaults
  );

  const {
    dateOfBirth,
    targetRetirementAge,
    propertyValue,
    privateAssetsValue,
    averageIncomeGrowthRate,
    taxRateRetention,
    annualInflationRate,
    averageEquityGrowthRate,
    averagePrivateAssetGrowthRate,
    averageRealEstateGrowthRate,
    averageEquityWithdrawalRate,
    socialSecurityIncome,
    averageRetirementEquityWithdrawalRate,
  } = defaults;

  const birthday = dateOfBirth?.toDate?.() ?? new Date();
  const birthdate = new Date(birthday);
  const currentAge = calculateAge(birthdate);
  const scalingFactor = 1 / divisorForPeriod(period);

  const results = {
    dateOfBirth,
    birthday,
    birthdate,
    currentAge,
    targetRetirementAge,
    propertyValue,
    privateAssetsValue,
    taxRateRetention,
    averageIncomeGrowthRate: averageIncomeGrowthRate
      ? averageIncomeGrowthRate ** scalingFactor
      : null,
    annualInflationRate: annualInflationRate ? annualInflationRate ** scalingFactor : null,
    averagePrivateAssetGrowthRate: annualInflationRate
      ? averagePrivateAssetGrowthRate ** scalingFactor
      : null,
    averageEquityGrowthRate: averageEquityGrowthRate
      ? averageEquityGrowthRate ** scalingFactor
      : null,
    averageRealEstateGrowthRate: averageRealEstateGrowthRate
      ? averageRealEstateGrowthRate ** scalingFactor
      : null,
    averageEquityWithdrawalRate,
    averageRetirementEquityWithdrawalRate,
    socialSecurityIncome,
  };

  return structuredClone(results);
}

function calculateAge(birthday) {
  const ageDifMs = Date.now() - birthday.getTime();
  const ageDate = new Date(ageDifMs); // milliseconds from epoch
  return Math.abs(ageDate.getUTCFullYear() - 1970);
}
